import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { fmtUGX, shortDate, shortDateTime } from "@/lib/format";
import {
  User, Wallet, GraduationCap, Bus, Heart, BookOpen, Users, Phone, Mail,
  Sparkles, Shield, Award, Utensils, TrendingUp, AlertTriangle,
  MapPin, CheckCircle2, Activity, Radio, Pencil, X, Save, Pin,
  MessageSquarePlus, ArrowUpRight, ArrowDownLeft, StickyNote, Trash2,
} from "lucide-react";
import { toast } from "sonner";

type Props = { studentId: string | null; open: boolean; onOpenChange: (o: boolean) => void };

export function StudentDetailModal({ studentId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["student-360", studentId],
    enabled: !!studentId && open,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_student_360", { _student_id: studentId! });
      if (error) throw error;
      return data as any;
    },
  });

  const s = data?.student;
  const feeTerms = (data?.fee_terms ?? []) as any[];
  const health = (data?.health_visits ?? []) as any[];
  const attendance = (data?.attendance ?? []) as any[];
  const transport = (data?.transport ?? []) as any[];
  const grades = (data?.grades ?? []) as any[];
  const meals = (data?.meals ?? []) as any[];
  const clubs = (data?.clubs ?? []) as any[];
  const guardian = data?.guardian;
  const controls = data?.controls;
  const wallet = data?.wallet ?? { balance_ugx: 0, daily_cap_ugx: 0 };
  const txns = (data?.recent_transactions ?? []) as any[];
  const notes = (data?.parent_notes ?? []) as any[];

  const totalOwed = feeTerms.reduce((a, f) => a + Math.max(0, (f.total_ugx ?? 0) - (f.paid_ugx ?? 0)), 0);
  const totalPaid = feeTerms.reduce((a, f) => a + (f.paid_ugx ?? 0), 0);
  const totalExpected = feeTerms.reduce((a, f) => a + (f.total_ugx ?? 0), 0);
  const collectionPct = totalExpected ? Math.round((totalPaid / totalExpected) * 100) : 0;

  const mealsTaken30 = meals.filter(m => m.taken).length;
  const mealsSkipped30 = meals.length - mealsTaken30;
  const avgGrade = grades.length ? Math.round(grades.reduce((a: number, g: any) => a + (g.score ?? 0), 0) / grades.length) : null;

  // ── Edit mode
  const [edit, setEdit] = useState<null | Partial<any>>(null);
  useEffect(() => { if (!open) setEdit(null); }, [open]);
  const saveEdit = useMutation({
    mutationFn: async () => {
      if (!studentId || !edit) return;
      const { error } = await supabase.rpc("update_student_profile" as never, { _student_id: studentId, _payload: edit } as never);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Profile updated"); setEdit(null); qc.invalidateQueries({ queryKey: ["student-360", studentId] }); },
    onError: (e: any) => toast.error("Update failed", { description: e.message }),
  });

  // ── Add parent note
  const [noteDraft, setNoteDraft] = useState({ note: "", category: "general", pinned: false });
  const addNote = useMutation({
    mutationFn: async () => {
      if (!studentId || !noteDraft.note.trim()) throw new Error("Type a note first.");
      const { error } = await supabase.rpc("add_parent_note" as never, {
        _student_id: studentId,
        _note: noteDraft.note.trim(),
        _category: noteDraft.category,
        _pinned: noteDraft.pinned,
        _visibility: "school",
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Note added");
      setNoteDraft({ note: "", category: "general", pinned: false });
      qc.invalidateQueries({ queryKey: ["student-360", studentId] });
    },
    onError: (e: any) => toast.error("Failed", { description: e.message }),
  });
  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("student_parent_notes" as never).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Note removed"); qc.invalidateQueries({ queryKey: ["student-360", studentId] }); },
  });

  const walletDaySpend = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return txns
      .filter(t => t.direction === "debit" && String(t.occurred_at).startsWith(today))
      .reduce((a, t) => a + (t.amount_ugx ?? 0), 0);
  }, [txns]);
  const dayCapPct = wallet.daily_cap_ugx ? Math.min(100, Math.round((walletDaySpend / wallet.daily_cap_ugx) * 100)) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden p-0 gap-0">
        <DialogTitle className="sr-only">Student profile</DialogTitle>
        {(isLoading || (isFetching && !data)) ? (
          <div className="flex flex-col items-center justify-center gap-3 p-16 text-sm text-muted-foreground">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Loading student profile…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-500" />
            <div className="text-sm font-medium">Couldn't load this student</div>
            <div className="max-w-md text-xs text-muted-foreground">{(error as Error).message}</div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => refetch()}>Try again</Button>
              <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        ) : !s ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No profile data available.</div>
        ) : (
          <>
            {/* HERO */}
            <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${s.color_hex ?? "#7B1416"}, #0f172a 130%)` }}>
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,.5) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,.3) 0, transparent 40%)" }} />
              <div className="relative flex items-start gap-5 p-6 text-white">
                <div className="grid h-24 w-24 place-items-center rounded-2xl bg-white/15 backdrop-blur text-3xl font-serif font-semibold ring-4 ring-white/20">
                  {edit ? (
                    <Input value={edit.initials ?? s.initials} onChange={(e) => setEdit({ ...edit, initials: e.target.value })} className="h-10 w-16 text-center bg-white/20 border-white/30 text-white" maxLength={3} />
                  ) : s.initials}
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/70">Student · {s.student_no}</div>
                  {edit ? (
                    <div className="mt-1 flex flex-wrap gap-2">
                      <Input value={edit.full_name ?? s.full_name} onChange={(e) => setEdit({ ...edit, full_name: e.target.value })} className="max-w-xs bg-white/20 border-white/30 text-white placeholder:text-white/60 font-serif text-lg" />
                      <Input value={edit.class_label ?? s.class_label ?? ""} onChange={(e) => setEdit({ ...edit, class_label: e.target.value })} placeholder="Class" className="max-w-[10rem] bg-white/20 border-white/30 text-white" />
                    </div>
                  ) : (
                    <>
                      <div className="mt-1 font-serif text-3xl font-semibold">{s.full_name}</div>
                      <div className="mt-1 text-sm text-white/80">{s.class_label} · {s.boarding ? "Boarding" : "Day scholar"}{s.bus_route ? ` · ${s.bus_route}` : ""}</div>
                    </>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge className="bg-white/15 hover:bg-white/25 text-white border-0">Wallet {fmtUGX(wallet.balance_ugx ?? 0)}</Badge>
                    <Badge className="bg-white/15 hover:bg-white/25 text-white border-0">Daily cap {fmtUGX(wallet.daily_cap_ugx ?? 0)}</Badge>
                    {s.status_label && <Badge className="bg-emerald-400/25 hover:bg-emerald-400/40 text-emerald-50 border-0">{s.status_label}</Badge>}
                    <div className="ml-auto flex gap-2">
                      {edit ? (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => setEdit(null)}><X className="mr-1 h-3 w-3" />Cancel</Button>
                          <Button size="sm" onClick={() => saveEdit.mutate()} disabled={saveEdit.isPending}>
                            <Save className="mr-1 h-3 w-3" />{saveEdit.isPending ? "Saving…" : "Save"}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => setEdit({})}>
                          <Pencil className="mr-1 h-3 w-3" />Edit profile
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="hidden md:grid grid-cols-3 gap-3">
                  <StatMini label="Collection" value={`${collectionPct}%`} icon={Wallet} />
                  <StatMini label="Attendance" value={`${attendance.length}`} icon={CheckCircle2} sub="checkpoints" />
                  <StatMini label="Grade avg" value={avgGrade != null ? `${avgGrade}` : "—"} icon={Award} />
                </div>
              </div>
            </div>

            <Tabs defaultValue="profile" className="flex flex-col flex-1 overflow-hidden">
              <div className="border-b bg-muted/30 px-4">
                <TabsList className="h-11 bg-transparent gap-1 overflow-x-auto">
                  <TabsTrigger value="profile"><User className="mr-1 h-3.5 w-3.5" />Profile</TabsTrigger>
                  <TabsTrigger value="wallet"><Wallet className="mr-1 h-3.5 w-3.5" />Wallet</TabsTrigger>
                  <TabsTrigger value="fees"><BookOpen className="mr-1 h-3.5 w-3.5" />Fees</TabsTrigger>
                  <TabsTrigger value="notes"><StickyNote className="mr-1 h-3.5 w-3.5" />Notes {notes.length ? <Badge variant="secondary" className="ml-1 text-[9px]">{notes.length}</Badge> : null}</TabsTrigger>
                  <TabsTrigger value="academics"><GraduationCap className="mr-1 h-3.5 w-3.5" />Academics</TabsTrigger>
                  <TabsTrigger value="attendance"><Activity className="mr-1 h-3.5 w-3.5" />Attendance</TabsTrigger>
                  <TabsTrigger value="health"><Heart className="mr-1 h-3.5 w-3.5" />Health</TabsTrigger>
                  <TabsTrigger value="transport"><Bus className="mr-1 h-3.5 w-3.5" />Transport</TabsTrigger>
                  <TabsTrigger value="life"><Sparkles className="mr-1 h-3.5 w-3.5" />Life</TabsTrigger>
                </TabsList>
              </div>

              <ScrollArea className="flex-1 max-h-[calc(92vh-260px)]">
                {/* PROFILE */}
                <TabsContent value="profile" className="p-5 space-y-4 m-0">
                  {edit && (
                    <Card className="border-primary/30 bg-primary/5 p-4">
                      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary"><Pencil className="h-3 w-3" />Editing profile</div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <FieldRow label="Student number">
                          <Input value={edit.student_no ?? s.student_no ?? ""} onChange={(e) => setEdit({ ...edit, student_no: e.target.value })} />
                        </FieldRow>
                        <FieldRow label="Status label">
                          <Input value={edit.status_label ?? s.status_label ?? ""} onChange={(e) => setEdit({ ...edit, status_label: e.target.value })} />
                        </FieldRow>
                        <FieldRow label="Bus route">
                          <Input value={edit.bus_route ?? s.bus_route ?? ""} onChange={(e) => setEdit({ ...edit, bus_route: e.target.value })} />
                        </FieldRow>
                        <FieldRow label="Daily cap (UGX)">
                          <Input type="number" value={edit.daily_cap_ugx ?? s.daily_cap_ugx ?? 0} onChange={(e) => setEdit({ ...edit, daily_cap_ugx: Number(e.target.value) })} />
                        </FieldRow>
                        <FieldRow label="Boarding">
                          <div className="flex h-10 items-center gap-2 rounded-md border px-3">
                            <Switch checked={edit.boarding ?? s.boarding} onCheckedChange={(v) => setEdit({ ...edit, boarding: v })} />
                            <span className="text-xs text-muted-foreground">{(edit.boarding ?? s.boarding) ? "Boarder" : "Day scholar"}</span>
                          </div>
                        </FieldRow>
                        <FieldRow label="Theme color">
                          <div className="flex items-center gap-2 rounded-md border p-1">
                            <input type="color" value={edit.color_hex ?? s.color_hex ?? "#7B1416"} onChange={(e) => setEdit({ ...edit, color_hex: e.target.value })} className="h-8 w-10 cursor-pointer rounded-sm border-0 bg-transparent" />
                            <Input value={edit.color_hex ?? s.color_hex ?? ""} onChange={(e) => setEdit({ ...edit, color_hex: e.target.value })} className="h-8 border-0 shadow-none" />
                          </div>
                        </FieldRow>
                      </div>
                    </Card>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card className="p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4" /> Guardian</div>
                      {guardian ? (
                        <div className="space-y-2 text-sm">
                          <div className="font-medium">{guardian.full_name ?? "—"}</div>
                          {guardian.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" />{guardian.phone}</div>}
                          {guardian.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" />{guardian.email}</div>}
                          <div className="pt-2 flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => toast.success("Message sent to guardian")}><Mail className="mr-1 h-3 w-3" />Message</Button>
                            <Button size="sm" variant="outline" onClick={() => toast.success("Call scheduled")}><Phone className="mr-1 h-3 w-3" />Call</Button>
                          </div>
                        </div>
                      ) : <div className="text-sm text-muted-foreground">No guardian linked yet.</div>}
                    </Card>
                    <Card className="p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Shield className="h-4 w-4" /> Controls</div>
                      {controls ? (
                        <div className="space-y-2 text-sm">
                          <Row label="Daily cap" value={fmtUGX(controls.daily_cap_ugx ?? 0)} />
                          <Row label="Window" value={`${controls.start_time ?? "—"} → ${controls.end_time ?? "—"}`} />
                          <Row label="Blocked" value={(controls.blocked_categories ?? []).join(", ") || "—"} />
                          <Row label="Keywords" value={(controls.blocked_keywords ?? []).join(", ") || "—"} />
                        </div>
                      ) : <div className="text-sm text-muted-foreground">No spending controls set.</div>}
                    </Card>
                  </div>

                  <div className="grid gap-3 md:grid-cols-4">
                    <BentoStat icon={Utensils} label="Meals taken (30d)" value={`${mealsTaken30}`} sub={`${mealsSkipped30} skipped`} />
                    <BentoStat icon={Award} label="Grade average" value={avgGrade != null ? `${avgGrade}` : "—"} sub={`${grades.length} entries`} />
                    <BentoStat icon={Radio} label="Clubs" value={`${clubs.length}`} sub={clubs.slice(0,2).map(c => c.club_name).filter(Boolean).join(", ") || "—"} />
                    <BentoStat icon={TrendingUp} label="Outstanding" value={fmtUGX(totalOwed)} sub={`of ${fmtUGX(totalExpected)}`} />
                  </div>
                </TabsContent>

                {/* WALLET */}
                <TabsContent value="wallet" className="p-5 space-y-4 m-0">
                  <div className="grid gap-3 md:grid-cols-3">
                    <Card className="bg-gradient-to-br from-emerald-600 to-emerald-800 p-5 text-white md:col-span-2 overflow-hidden relative">
                      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 90% 20%, rgba(255,255,255,.4) 0, transparent 30%)" }} />
                      <div className="relative">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">SchoolOS Pay wallet</div>
                        <div className="mt-1 font-serif text-4xl">{fmtUGX(wallet.balance_ugx ?? 0)}</div>
                        <div className="mt-3 text-xs text-white/75">Today's spend</div>
                        <div className="mt-1 flex items-center gap-3">
                          <Progress value={dayCapPct} className="h-2 flex-1 bg-white/20" />
                          <span className="text-xs font-mono">{fmtUGX(walletDaySpend)} / {fmtUGX(wallet.daily_cap_ugx ?? 0)}</span>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => toast.success("Top-up link sent to guardian")}>Top up</Button>
                          <Button size="sm" variant="secondary" onClick={() => toast.success("Card frozen")}>Freeze card</Button>
                          <Button size="sm" variant="secondary" onClick={() => toast.success("Statement generated")}>Statement</Button>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">This month</div>
                      <SpendPill label="Merchant" value={sumBy(txns, "direction", "debit", "kind", "purchase")} tone="emerald" />
                      <SpendPill label="Canteen" value={sumBy(txns, "direction", "debit", "kind", "canteen")} tone="amber" />
                      <SpendPill label="Top-ups" value={sumBy(txns, "direction", "credit")} tone="indigo" />
                    </Card>
                  </div>

                  <Card>
                    <div className="border-b p-4 text-sm font-semibold flex items-center justify-between">
                      <span>Recent transactions</span>
                      <Badge variant="outline">{txns.length}</Badge>
                    </div>
                    {txns.length === 0 ? (
                      <div className="p-8 text-center text-sm text-muted-foreground">
                        <Wallet className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                        No transactions yet. Once the guardian tops up or the student pays a merchant, activity appears here.
                      </div>
                    ) : (
                      <div className="divide-y">
                        {txns.map((t) => {
                          const isCredit = t.direction === "credit";
                          return (
                            <div key={t.id} className="flex items-center gap-3 p-3 text-sm">
                              <div className={`grid h-9 w-9 place-items-center rounded-full ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                                {isCredit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{t.title ?? t.kind ?? "Transaction"}</div>
                                <div className="truncate text-xs text-muted-foreground">{t.subtitle ?? t.ref ?? shortDateTime(t.occurred_at)}</div>
                              </div>
                              <div className="text-right">
                                <div className={`font-mono ${isCredit ? "text-emerald-700" : ""}`}>{isCredit ? "+" : "−"}{fmtUGX(t.amount_ugx ?? 0)}</div>
                                <div className="text-[10px] text-muted-foreground">{shortDate(t.occurred_at)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                {/* FEES */}
                <TabsContent value="fees" className="p-5 space-y-4 m-0">
                  <Card className="p-4">
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-semibold">Term collection</span>
                      <span className="text-muted-foreground">{fmtUGX(totalPaid)} of {fmtUGX(totalExpected)}</span>
                    </div>
                    <Progress value={collectionPct} className="h-2.5" />
                    <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                      <Mini label="Paid" v={fmtUGX(totalPaid)} tone="ok" />
                      <Mini label="Outstanding" v={fmtUGX(totalOwed)} tone="warn" />
                      <Mini label="Terms" v={`${feeTerms.length}`} />
                    </div>
                  </Card>
                  <Card>
                    <div className="border-b p-4 text-sm font-semibold">Fee terms</div>
                    <div className="divide-y">
                      {feeTerms.map((f) => {
                        const pct = f.total_ugx ? Math.round((f.paid_ugx / f.total_ugx) * 100) : 0;
                        return (
                          <div key={f.id} className="p-4 text-sm">
                            <div className="flex items-center justify-between">
                              <div className="font-medium">{f.term_label}</div>
                              <div className="text-xs text-muted-foreground">Due {f.next_due ? shortDate(f.next_due) : "—"}</div>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">Installment {f.installment}/{f.of_installments} · {f.autopay_method ?? "manual"}</div>
                            <div className="mt-2 grid grid-cols-[1fr_auto] items-center gap-3">
                              <Progress value={pct} className="h-2" />
                              <div className="font-mono text-xs">{fmtUGX(f.paid_ugx)} / {fmtUGX(f.total_ugx)}</div>
                            </div>
                          </div>
                        );
                      })}
                      {!feeTerms.length && <div className="p-6 text-center text-sm text-muted-foreground">No fee terms.</div>}
                    </div>
                  </Card>
                </TabsContent>

                {/* NOTES */}
                <TabsContent value="notes" className="p-5 space-y-4 m-0">
                  <Card className="p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><MessageSquarePlus className="h-4 w-4" />Add a note</div>
                    <Textarea rows={3} placeholder="Medical alert, pickup instruction, praise, behavior note…" value={noteDraft.note} onChange={(e) => setNoteDraft({ ...noteDraft, note: e.target.value })} />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Select value={noteDraft.category} onValueChange={(v) => setNoteDraft({ ...noteDraft, category: v })}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="medical">Medical</SelectItem>
                          <SelectItem value="behavior">Behavior</SelectItem>
                          <SelectItem value="academic">Academic</SelectItem>
                          <SelectItem value="logistics">Logistics</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs">
                        <Switch checked={noteDraft.pinned} onCheckedChange={(v) => setNoteDraft({ ...noteDraft, pinned: v })} />Pin
                      </label>
                      <Button size="sm" className="ml-auto" onClick={() => addNote.mutate()} disabled={addNote.isPending || !noteDraft.note.trim()}>
                        <MessageSquarePlus className="mr-1 h-3.5 w-3.5" />{addNote.isPending ? "Saving…" : "Post note"}
                      </Button>
                    </div>
                  </Card>
                  <div className="space-y-2">
                    {notes.length === 0 && (
                      <Card className="p-8 text-center text-sm text-muted-foreground">
                        <StickyNote className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                        No notes yet — nurses, teachers and guardians can leave context here.
                      </Card>
                    )}
                    {notes.map((n) => (
                      <Card key={n.id} className={`p-3 ${n.pinned ? "border-amber-300 bg-amber-50/50" : ""}`}>
                        <div className="flex items-start gap-3">
                          <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold uppercase ${roleColor(n.author_role)}`}>
                            {(n.author_name ?? n.author_role ?? "?").slice(0,1)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-medium">{n.author_name ?? n.author_role}</span>
                              <Badge variant="outline" className="text-[10px] capitalize">{n.category}</Badge>
                              {n.pinned && <Badge className="bg-amber-500/20 text-amber-800 border-0 text-[10px]"><Pin className="mr-0.5 h-2.5 w-2.5" />pinned</Badge>}
                              <span className="ml-auto text-[10px] text-muted-foreground">{shortDateTime(n.created_at)}</span>
                              <button className="rounded p-0.5 text-muted-foreground hover:text-destructive" onClick={() => deleteNote.mutate(n.id)}>
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                            <div className="mt-1 text-sm whitespace-pre-wrap">{n.note}</div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* ACADEMICS */}
                <TabsContent value="academics" className="p-5 space-y-3 m-0">
                  <Card>
                    <div className="border-b p-4 text-sm font-semibold">Recent grades</div>
                    <div className="divide-y max-h-[420px] overflow-auto">
                      {grades.map((g) => (
                        <div key={g.id} className="flex items-center justify-between p-3 text-sm">
                          <div>
                            <div className="font-medium">{g.assessment_title ?? g.assessment_type ?? "Assessment"}</div>
                            <div className="text-xs text-muted-foreground">{shortDate(g.created_at)}</div>
                          </div>
                          <Badge variant={g.score >= 70 ? "secondary" : g.score >= 50 ? "outline" : "destructive"}>{g.score ?? "—"}{g.max_score ? `/${g.max_score}` : ""}</Badge>
                        </div>
                      ))}
                      {!grades.length && <div className="p-6 text-center text-sm text-muted-foreground">No grades captured yet.</div>}
                    </div>
                  </Card>
                </TabsContent>

                {/* ATTENDANCE */}
                <TabsContent value="attendance" className="p-5 space-y-3 m-0">
                  <Card>
                    <div className="border-b p-4 text-sm font-semibold">Recent checkpoints</div>
                    <div className="divide-y max-h-[420px] overflow-auto">
                      {attendance.map((a) => (
                        <div key={a.id} className="flex items-center gap-3 p-3 text-sm">
                          <div className="grid h-8 w-8 place-items-center rounded-full bg-emerald-100 text-emerald-700"><MapPin className="h-4 w-4" /></div>
                          <div className="flex-1">
                            <div className="font-medium capitalize">{a.kind}</div>
                            <div className="text-xs text-muted-foreground">{a.location ?? "—"}</div>
                          </div>
                          <div className="text-xs text-muted-foreground">{shortDateTime(a.scanned_at)}</div>
                        </div>
                      ))}
                      {!attendance.length && <div className="p-6 text-center text-sm text-muted-foreground">No checkpoints logged.</div>}
                    </div>
                  </Card>
                </TabsContent>

                {/* HEALTH */}
                <TabsContent value="health" className="p-5 space-y-3 m-0">
                  <Card>
                    <div className="border-b p-4 text-sm font-semibold">Health visits</div>
                    <div className="divide-y">
                      {health.map((h) => (
                        <div key={h.id} className="p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{h.reason}</div>
                            <Badge variant={h.status === "resolved" ? "secondary" : "outline"} className="capitalize">{h.status}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">{shortDate(h.visit_date)}</div>
                          {h.nurse_note && <div className="mt-1 text-xs text-muted-foreground">Nurse: {h.nurse_note}</div>}
                        </div>
                      ))}
                      {!health.length && <div className="p-6 text-center text-sm text-muted-foreground">No health records.</div>}
                    </div>
                  </Card>
                </TabsContent>

                {/* TRANSPORT */}
                <TabsContent value="transport" className="p-5 space-y-3 m-0">
                  <Card>
                    <div className="border-b p-4 text-sm font-semibold">Transport schedule</div>
                    <div className="grid gap-px bg-border md:grid-cols-2">
                      {transport.map((t) => (
                        <div key={t.id} className="bg-card p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{t.route} · <span className="uppercase text-xs text-muted-foreground">{t.direction}</span></div>
                            <Badge variant="outline">Day {t.day_of_week}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">{t.pickup_time} · {t.stop}</div>
                        </div>
                      ))}
                      {!transport.length && <div className="bg-card p-6 text-center text-sm text-muted-foreground md:col-span-2">No transport schedule.</div>}
                    </div>
                  </Card>
                </TabsContent>

                {/* LIFE */}
                <TabsContent value="life" className="p-5 space-y-3 m-0">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Card className="p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Radio className="h-4 w-4" />Clubs & societies</div>
                      {clubs.length ? (
                        <div className="space-y-2">
                          {clubs.map((c) => (
                            <div key={c.id} className="flex items-center justify-between text-sm">
                              <div>
                                <div className="font-medium">{c.club_name ?? "Club"}</div>
                                <div className="text-xs text-muted-foreground capitalize">{c.category ?? "—"}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">joined {shortDate(c.joined_at)}</div>
                            </div>
                          ))}
                        </div>
                      ) : <div className="text-sm text-muted-foreground">Not enrolled in any clubs yet.</div>}
                    </Card>
                    <Card className="p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Utensils className="h-4 w-4" />Meals · last 30 days</div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><div className="text-2xl font-serif">{mealsTaken30}</div><div className="text-xs text-muted-foreground">taken</div></div>
                        <div><div className="text-2xl font-serif text-amber-600">{mealsSkipped30}</div><div className="text-xs text-muted-foreground">skipped</div></div>
                      </div>
                      {mealsSkipped30 >= 3 && (
                        <div className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                          Skipped {mealsSkipped30} meals — consider notifying the matron.
                        </div>
                      )}
                    </Card>
                  </div>
                </TabsContent>
              </ScrollArea>

              <div className="flex items-center justify-between border-t bg-muted/30 p-3">
                <div className="text-xs text-muted-foreground">Student ID · <span className="font-mono">{s.id.slice(0, 8)}</span></div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toast.success("Statement queued")}><BookOpen className="mr-1 h-3.5 w-3.5" />Statement</Button>
                  <Button size="sm" variant="outline" onClick={() => toast.success("Parent reminder sent")}><Mail className="mr-1 h-3.5 w-3.5" />Nudge parent</Button>
                  <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
                </div>
              </div>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function sumBy(rows: any[], k1: string, v1: string, k2?: string, v2?: string) {
  const out = rows.filter((r) => r[k1] === v1 && (!k2 || r[k2!] === v2)).reduce((a, r) => a + (r.amount_ugx ?? 0), 0);
  return fmtUGX(out);
}

function roleColor(role?: string) {
  switch (role) {
    case "admin": return "bg-primary/15 text-primary";
    case "teacher": return "bg-indigo-100 text-indigo-700";
    case "nurse": return "bg-rose-100 text-rose-700";
    case "matron": return "bg-amber-100 text-amber-700";
    default: return "bg-emerald-100 text-emerald-700";
  }
}

function SpendPill({ label, value, tone }: { label: string; value: string; tone: "emerald" | "amber" | "indigo" }) {
  const map = { emerald: "bg-emerald-500/10 text-emerald-700", amber: "bg-amber-500/10 text-amber-700", indigo: "bg-indigo-500/10 text-indigo-700" };
  return (
    <div className={`mb-1.5 flex items-center justify-between rounded-md px-2.5 py-1.5 text-xs ${map[tone]}`}>
      <span>{label}</span><span className="font-mono">{value}</span>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function StatMini({ label, value, sub, icon: Icon }: { label: string; value: string; sub?: string; icon: any }) {
  return (
    <div className="rounded-xl bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/70"><Icon className="h-3 w-3" />{label}</div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
      {sub && <div className="text-[10px] text-white/60">{sub}</div>}
    </div>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{label}</span><span className="font-medium text-right">{value}</span></div>;
}
function Mini({ label, v, tone }: { label: string; v: string; tone?: "ok" | "warn" }) {
  return (
    <div className="rounded-md border bg-muted/30 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono text-sm ${tone === "warn" ? "text-amber-600" : tone === "ok" ? "text-emerald-600" : ""}`}>{v}</div>
    </div>
  );
}
function BentoStat({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3 w-3" />{label}</div>
      <div className="mt-1 font-serif text-xl">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}
